import React, { useState } from 'react';
import { Manifest, PublicationType, BlockType } from '../../types';
import { Info, Check, X, FileText, Image as ImageIcon, File as FileIcon, BarChart2, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { getBlockTypeName } from '../../utils/manifestUtils';

interface ManifestInfoProps {
    manifest: Manifest;
    publicationType: PublicationType;
}

export const ManifestInfo: React.FC<ManifestInfoProps> = ({ manifest, publicationType }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const rules = manifest.rules;
    const variants = rules.publicationTypes[publicationType]?.variants || [];

    const getIcon = (type: BlockType) => {
        switch(type) {
            case 'text': return <FileText className="w-3 h-3" />;
            case 'media': return <ImageIcon className="w-3 h-3" />;
            case 'file': return <FileIcon className="w-3 h-3" />;
            case 'poll': return <BarChart2 className="w-3 h-3" />;
        }
    };

    return (
        <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden text-sm transition-all duration-300">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full bg-white px-4 py-3 border-b border-slate-200 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-500" />
                    <h4 className="font-bold text-slate-700">Правила публикации</h4>
                </div>
                {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            
            {isExpanded && (
                <div className="p-4 space-y-4 animate-in slide-in-from-top-2 fade-in duration-200">
                    {/* 1. Limits Grid */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white p-2.5 rounded-lg border border-slate-100 shadow-sm">
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">Текст</div>
                            <div className="font-mono font-bold text-slate-700">
                                {rules.text?.maxLength || 0} <span className="text-[10px] font-normal text-slate-400">симв.</span>
                            </div>
                        </div>
                        
                        <div className="bg-white p-2.5 rounded-lg border border-slate-100 shadow-sm">
                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1">Медиа</div>
                            <div className="font-mono font-bold text-slate-700">
                                {rules.photo?.maxItems || 0} <span className="text-[10px] font-normal text-slate-400">файлов</span>
                            </div>
                        </div>
                    </div>

                    {/* 2. Supported Combinations */}
                    <div>
                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 flex items-center justify-between">
                            Разрешенные комбинации
                        </div>
                        
                        {variants.length > 0 ? (
                            <div className="space-y-2">
                                {variants.map((v, idx) => (
                                    <div key={v.id} className="flex items-start gap-2 text-xs bg-white p-2 rounded-lg border border-slate-100">
                                        <div className="mt-0.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                                        <div>
                                            <div className="font-bold text-slate-700 mb-1">{v.label}</div>
                                            <div className="flex flex-wrap gap-1.5">
                                                {v.allowedBlocks.map(b => (
                                                    <span key={b} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] border border-slate-200">
                                                        {getIcon(b)}
                                                        {getBlockTypeName(b)}
                                                    </span>
                                                ))}
                                            </div>
                                            {v.constraints?.media && (
                                                <div className="mt-1 text-[9px] text-slate-400">
                                                    {v.constraints.media.maxItems === 1 ? 'Только 1 медиафайл' : `До ${v.constraints.media.maxItems} медиафайлов`}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="p-3 bg-white border border-slate-100 rounded-lg text-slate-500 text-xs italic text-center">
                                Стандартные ограничения (все блоки разрешены)
                            </div>
                        )}
                    </div>

                    {/* 3. Specific Warnings */}
                    {rules.photo?.allowMixedTypesInGroup === false && (
                        <div className="flex items-start gap-2 p-2 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-100 text-xs">
                            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                            <div>Нельзя смешивать фото и видео в одном посте.</div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};